import { useEffect, useState } from "react";

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

export function FacebookPixel() {
  // Only render on client side to avoid hydration issues
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    // Only run Facebook Pixel initialization on the client
    if (!isClient) return;

    // Facebook Pixel Code
    (function(f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', '785646297364344');
    window.fbq('track', 'PageView');
  }, [isClient]);

  return null;
}

// Helper function to track custom events
export function trackFacebookEvent(
  eventName: string, 
  parameters?: Record<string, any>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
}

// Helper function to track standard events
export function trackStandardEvent(
  eventName: 'ViewContent' | 'Search' | 'AddToCart' | 'AddToWishlist' | 'InitiateCheckout' | 'AddPaymentInfo' | 'Purchase' | 'Lead' | 'CompleteRegistration',
  parameters?: Record<string, any>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, parameters);
  }
}

// Generate event ID for deduplication with server events
function generateEventId(eventName: string, userId?: string): string {
  const timestamp = Date.now();
  const id = userId || 'anonymous';
  // Simple hash function for browser
  const hashString = `${eventName}-${id}-${timestamp}`;
  let hash = 0;
  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36).substring(0, 36);
}

// Track ViewContent event for station visits
export function trackStationVisit(stationData: {
  stationId: string;
  stationName: string;
  broadcasterUsername: string;
  stationSlug: string;
  isLive: boolean;
  viewerCount?: number;
  userId?: string;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('ViewContent', stationData.userId);
    
    // Track standard ViewContent event with event_id for deduplication
    window.fbq('track', 'ViewContent', {
      content_ids: [stationData.stationId],
      content_type: 'station',
      content_name: stationData.stationName,
      content_category: 'radio_station',
      value: stationData.viewerCount || 0,
      currency: 'USD',
    }, {
      eventID: eventId // This will be matched with server-side event_id
    });
  }
}

// Track CompleteRegistration event
export function trackRegistration(userData: {
  userId: string;
  email?: string;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('CompleteRegistration', userData.userId);
    
    window.fbq('track', 'CompleteRegistration', {
      status: 'completed',
      content_name: 'User Registration',
    }, {
      eventID: eventId
    });
  }
}

// Track StartTrial event
export function trackStartTrial(trialData: {
  userId?: string;
  trialType?: string;
  value?: number;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('StartTrial', trialData.userId);
    
    window.fbq('track', 'StartTrial', {
      content_name: trialData.trialType || 'Broadcaster Trial',
      predicted_ltv: trialData.value || 9.99,
    }, {
      eventID: eventId
    });
  }
}

// Track InitiateCheckout event
export function trackInitiateCheckout(checkoutData: {
  userId?: string;
  productId?: string;
  productName?: string;
  value?: number;
  currency?: string;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('InitiateCheckout', checkoutData.userId);
    
    window.fbq('track', 'InitiateCheckout', {
      content_ids: checkoutData.productId ? [checkoutData.productId] : undefined,
      content_name: checkoutData.productName,
      currency: checkoutData.currency || 'USD',
      value: checkoutData.value,
      num_items: 1,
    }, {
      eventID: eventId
    });
  }
}

// Track Purchase event
export function trackPurchase(purchaseData: {
  userId?: string;
  orderId?: string;
  value: number;
  currency?: string;
  productId?: string;
  productName?: string;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('Purchase', purchaseData.userId);
    
    window.fbq('track', 'Purchase', {
      currency: purchaseData.currency || 'USD',
      value: purchaseData.value,
      order_id: purchaseData.orderId,
      content_ids: purchaseData.productId ? [purchaseData.productId] : undefined,
      content_name: purchaseData.productName,
      content_type: 'product',
    }, {
      eventID: eventId
    });
  }
}

// Track Lead event
export function trackLead(leadData: {
  userId?: string;
  leadType?: string;
}) {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = generateEventId('Lead', leadData.userId);
    
    window.fbq('track', 'Lead', {
      content_name: leadData.leadType || 'General Inquiry',
    }, {
      eventID: eventId
    });
  }
}